import { apiClient } from './api';

export interface Meeting {
  id: number;
  title: string;
  date: string;
  time: string;
  duration: number;
  location: string;
  attendees: string[];
  workshops_info: Array<{
    id: number;
    title: string;
    department_name: string;
  }>;
  workshop_count: number;
  attendee_count: number;
  message: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  created_by: number;
  created_by_name: string;
  created_by_email?: string;
  created_at: string;
  updated_at: string;
  invitations_sent: boolean;
  invitations_sent_at?: string;
  attachments?: MeetingAttachment[];
  invitations?: MeetingInvitation[];
}

export interface MeetingAttachment {
  id: number;
  file: string;
  file_url: string;
  original_filename: string;
  file_size: number;
  file_size_mb: number;
  content_type: string;
  uploaded_by: number;
  uploaded_by_name: string;
  uploaded_at: string;
}

export interface MeetingInvitation {
  id: number;
  email: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'accepted' | 'declined';
  sent_at?: string;
  response_at?: string;
  error_message?: string;
}

export interface WorkshopForMeeting {
  id: number;
  title: string;
  department_name: string;
}

export interface MeetingCreateData {
  title: string;
  date: string;
  time: string;
  duration: number;
  location: string;
  attendees: number[];
  workshops: number[];
  message: string;
  attachments?: File[];
}

export interface MeetingUpdateData extends Partial<MeetingCreateData> {
  status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
}

export interface MeetingFilters {
  start_date?: string;
  end_date?: string;
  status?: string;
  workshop?: number;
  search?: string;
}

export class MeetingService {
  /**
   * Get all meetings with optional filters
   */
  static async getMeetings(filters?: MeetingFilters): Promise<Meeting[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
      }
      
      const response = await apiClient.get(`/meetings/?${params.toString()}`);
      console.log('API Response:', response);
      console.log('Response data:', response.data);
      
      // Ensure we return an array
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Handle paginated response
        return response.data.results;
      } else {
        console.warn('API did not return an array:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      throw error;
    }
  }

  /**
   * Get a specific meeting by ID
   */
  static async getMeeting(id: number): Promise<Meeting> {
    const response = await apiClient.get(`/meetings/${id}/`);
    return response.data;
  }

  /**
   * Create a new meeting
   */
  static async createMeeting(data: MeetingCreateData): Promise<Meeting> {
    const response = await apiClient.post('/meetings/', data);
    return response.data;
  }

  /**
   * Update an existing meeting
   */
  static async updateMeeting(id: number, data: MeetingUpdateData): Promise<Meeting> {
    const response = await apiClient.patch(`/meetings/${id}/`, data);
    return response.data;
  }

  /**
   * Delete a meeting
   */
  static async deleteMeeting(id: number): Promise<void> {
    await apiClient.delete(`/meetings/${id}/`);
  }

  /**
   * Send invitations for a meeting
   */
  static async sendInvitations(id: number): Promise<{ message: string; sent_count: number }> {
    const response = await apiClient.post(`/meetings/${id}/send_invitations/`);
    return response.data;
  }

  /**
   * Resend invitations for a meeting
   */
  static async resendInvitations(id: number): Promise<{ message: string; sent_count: number }> {
    const response = await apiClient.post(`/meetings/${id}/resend_invitations/`);
    return response.data;
  }

  /**
   * Get upcoming meetings
   */
  static async getUpcomingMeetings(): Promise<Meeting[]> {
    const response = await apiClient.get('/meetings/upcoming/');
    return response.data;
  }

  /**
   * Get meetings created by current user
   */
  static async getMyMeetings(): Promise<Meeting[]> {
    const response = await apiClient.get('/meetings/my_meetings/');
    return response.data;
  }

  /**
   * Get meeting statistics
   */
  static async getMeetingStatistics(): Promise<{
    total_meetings: number;
    upcoming_meetings: number;
    completed_meetings: number;
    cancelled_meetings: number;
    status_breakdown: Record<string, number>;
  }> {
    const response = await apiClient.get('/meetings/statistics/');
    return response.data;
  }

  /**
   * Upload attachment to a meeting
   */
  static async uploadAttachment(meetingId: number, file: File, originalFilename?: string): Promise<MeetingAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    if (originalFilename) {
      formData.append('original_filename', originalFilename);
    }

    const response = await apiClient.post(`/meetings/${meetingId}/upload_attachment/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }


  /**
   * Delete an attachment from a meeting
   */
  static async deleteAttachment(meetingId: number, attachmentId: number): Promise<void> {
    await apiClient.delete(`/meetings/${meetingId}/attachments/${attachmentId}/`);
  }

  /**
   * Get workshops available for meeting selection
   */
  static async getWorkshopsForMeeting(): Promise<WorkshopForMeeting[]> {
    try {
      const response = await apiClient.get('/workshops/?page_size=1000');
      console.log('Workshops API Response:', response);
      console.log('Workshops Response data:', response.data);
      
      // Map the workshops to the expected format
      const workshops = response.data.results.map((workshop: any) => ({
        id: workshop.id,
        title: workshop.title,
        department_name: workshop.department_name
      }));
      
      return workshops;
    } catch (error) {
      console.error('Error fetching workshops for meetings:', error);
      throw error;
    }
  }

  /**
   * Format meeting time display
   */
  static formatTime(timeString: string, duration: number): string {
    const [hours, minutes] = timeString.split(':');
    const startTime = new Date();
    startTime.setHours(parseInt(hours), parseInt(minutes));
    
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + duration);
    
    return `${startTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })} - ${endTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })}`;
  }

  /**
   * Format meeting date display
   */
  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Group meetings by date
   */
  static groupMeetingsByDate(meetings: Meeting[]): { [key: string]: Meeting[] } {
    const grouped: { [key: string]: Meeting[] } = {};
    
    // Safety check: ensure meetings is an array
    if (!Array.isArray(meetings)) {
      console.warn('groupMeetingsByDate: meetings is not an array:', meetings);
      return grouped;
    }
    
    meetings.forEach(meeting => {
      if (!grouped[meeting.date]) {
        grouped[meeting.date] = [];
      }
      grouped[meeting.date].push(meeting);
    });
    
    // Sort meetings within each date by time
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.time.localeCompare(b.time));
    });
    
    return grouped;
  }

  /**
   * Get status color for UI display
   */
  static getStatusColor(status: string): string {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Get status display name
   */
  static getStatusDisplayName(status: string): string {
    switch (status) {
      case 'scheduled':
        return 'Scheduled';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  }
}
